import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi } from "@/services/adminApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MessageSquare, DollarSign, MessageCircle, CreditCard, Crown, Clock, Activity, Timer, ChevronDown, ChevronRight, List } from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table";
import type { User, UserChat, ServiceUsage, UsageSession, UserUsageStatus, ActiveSessionResponse, SessionActivityEvent } from "../types/admin";
import { SuperadminGrantCard } from "@/components/users/SuperadminGrantCard";

type TabType = "chats" | "service-usage" | "sessions";

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [chats, setChats] = useState<UserChat[]>([]);
  const [serviceUsage, setServiceUsage] = useState<ServiceUsage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [isLoadingServiceUsage, setIsLoadingServiceUsage] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("chats");
  const [chatsDaysFilter, setChatsDaysFilter] = useState<string>("all");
  const [serviceUsageDaysFilter, setServiceUsageDaysFilter] = useState<string>("all");
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);

  const [sessions, setSessions] = useState<UsageSession[]>([]);
  const [activeSession, setActiveSession] = useState<ActiveSessionResponse | null>(null);
  const [usageStatus, setUsageStatus] = useState<UserUsageStatus | null>(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingActiveSession, setIsLoadingActiveSession] = useState(false);
  const [showActiveSessionActivity, setShowActiveSessionActivity] = useState(false);
  const [activeSessionActivity, setActiveSessionActivity] = useState<SessionActivityEvent[]>([]);
  const [isLoadingActiveActivity, setIsLoadingActiveActivity] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [sessionActivities, setSessionActivities] = useState<Record<string, SessionActivityEvent[]>>({});
  const [loadingSessionActivities, setLoadingSessionActivities] = useState<Record<string, boolean>>({});
  
  const [sessionsPagination, setSessionsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const [chatsPagination, setChatsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const [serviceUsagePagination, setServiceUsagePagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    if (id) {
      fetchUserDetails(id);
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === "chats") {
      fetchUserChats(id, chatsPagination.page, chatsPagination.limit, chatsDaysFilter);
    }
  }, [id, chatsPagination.page, chatsPagination.limit, activeTab, chatsDaysFilter]);

  useEffect(() => {
    if (id && activeTab === "service-usage") {
      fetchUserServiceUsage(id, serviceUsagePagination.page, serviceUsagePagination.limit, serviceUsageDaysFilter);
    }
  }, [id, serviceUsagePagination.page, serviceUsagePagination.limit, activeTab, serviceUsageDaysFilter]);

  useEffect(() => {
    if (id && activeTab === "sessions") {
      fetchUserSessions(id, sessionsPagination.page, sessionsPagination.limit);
      fetchUserActiveSession(id);
      fetchUserUsageStatus(id);
    }
  }, [id, sessionsPagination.page, sessionsPagination.limit, activeTab]);

  const fetchUserDetails = async (userId: string) => {
    try {
      setIsLoading(true);
      const userData = await adminApi.getUser(userId);
      setUser(userData);
      setSelectedPlan(userData.subscriptionPlan || "free");
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // The projected isSuperadmin flag lands via the outbox a few seconds after a
  // grant change; poll briefly so the badge follows without a manual reload.
  const refreshAfterGrantChange = async () => {
    if (!id) return;
    const before = user?.isSuperadmin;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const fresh = await adminApi.getUser(id);
      setUser(fresh);
      if (fresh.isSuperadmin !== before) return;
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
  };

  const handlePlanUpdate = async () => {
    if (!id || !selectedPlan || selectedPlan === user?.subscriptionPlan) return;
    try {
      setIsUpdatingPlan(true);
      await adminApi.updateUserPlan(id, selectedPlan);
      setUser((prev) => prev ? { ...prev, subscriptionPlan: selectedPlan as 'free' | 'pro' | 'pro_max', planOverrideByAdmin: true } : prev);
    } catch (error) {
      console.error("Failed to update user plan:", error);
    } finally {
      setIsUpdatingPlan(false);
    }
  };

  const fetchUserChats = async (userId: string, page: number, limit: number, daysFilter: string) => {
    try {
      setIsLoadingChats(true);
      const days = daysFilter === "all" ? undefined : parseInt(daysFilter);
      const response = await adminApi.getUserChats(userId, page, limit, days);
      setChats(response.chats);
      setChatsPagination(response.pagination);
    } catch (error) {
      console.error("Failed to fetch user chats:", error);
    } finally {
      setIsLoadingChats(false);
    }
  };

  const fetchUserServiceUsage = async (userId: string, page: number, limit: number, daysFilter: string) => {
    try {
      setIsLoadingServiceUsage(true);
      const days = daysFilter === "all" ? undefined : parseInt(daysFilter);
      const response = await adminApi.getUserServiceUsage(userId, page, limit, days);
      setServiceUsage(response.serviceUsage);
      setServiceUsagePagination(response.pagination);
    } catch (error) {
      console.error("Failed to fetch user service usage:", error);
    } finally {
      setIsLoadingServiceUsage(false);
    }
  };

  const fetchUserSessions = async (userId: string, page: number, limit: number) => {
    try {
      setIsLoadingSessions(true);
      const response = await adminApi.getUserSessions(userId, page, limit);
      setSessions(response.sessions);
      setSessionsPagination(response.pagination);
    } catch (error) {
      console.error("Failed to fetch user sessions:", error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const fetchUserActiveSession = async (userId: string) => {
    try {
      setIsLoadingActiveSession(true);
      const response = await adminApi.getUserActiveSession(userId);
      setActiveSession(response);
    } catch (error) {
      console.error("Failed to fetch active session:", error);
    } finally {
      setIsLoadingActiveSession(false);
    }
  };

  const fetchUserUsageStatus = async (userId: string) => {
    try {
      const response = await adminApi.getUserUsageStatus(userId);
      setUsageStatus(response);
    } catch (error) {
      console.error("Failed to fetch usage status:", error);
    }
  };

  const fetchSessionActivity = async (userId: string, sessionId: string) => {
    if (sessionActivities[sessionId]) return;
    try {
      setLoadingSessionActivities((prev) => ({ ...prev, [sessionId]: true }));
      const response = await adminApi.getSessionActivity(userId, sessionId, 1, 50);
      setSessionActivities((prev) => ({ ...prev, [sessionId]: response.events }));
    } catch (error) {
      console.error("Failed to fetch session activity:", error);
    } finally {
      setLoadingSessionActivities((prev) => ({ ...prev, [sessionId]: false }));
    }
  };

  const fetchActiveSessionActivity = async (userId: string, sessionId: string) => {
    if (activeSessionActivity.length > 0) return;
    try {
      setIsLoadingActiveActivity(true);
      const response = await adminApi.getSessionActivity(userId, sessionId, 1, 10);
      setActiveSessionActivity(response.events);
    } catch (error) {
      console.error("Failed to fetch active session activity:", error);
    } finally {
      setIsLoadingActiveActivity(false);
    }
  };

  const handleSessionsPageChange = (newPage: number) => {
    setSessionsPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleChatsPageChange = (newPage: number) => {
    setChatsPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleServiceUsagePageChange = (newPage: number) => {
    setServiceUsagePagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleChatsDaysFilterChange = (value: string) => {
    setChatsDaysFilter(value);
    setChatsPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleServiceUsageDaysFilterChange = (value: string) => {
    setServiceUsageDaysFilter(value);
    setServiceUsagePagination((prev) => ({ ...prev, page: 1 }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-gray-500">User not found</p>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/users")}
            className="mt-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  const totalCost = chats.reduce((sum, chat) => sum + chat.totalCost, 0);
  const totalMessages = chats.reduce((sum, chat) => sum + chat.numberOfMessages, 0);
  const totalChats = chatsPagination.total;
  const totalCredits = serviceUsage.reduce((sum, usage) => sum + usage.creditsUsed, 0);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/users")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              {user.name || user.email}
            </h1>
            <p className="text-gray-600 mt-1">{user.email}</p>
            {user.organization && (
              <p className="text-gray-500 text-sm">
                {user.organization.name} • @{user.organization.emailDomain}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Badge variant={user.role === "member" ? "default" : "warning"}>
            {user.role}
          </Badge>
          {user.isSuperadmin && <Badge variant="error">superadmin</Badge>}
          <Badge variant={user.verified ? "success" : "default"}>
            {user.verified ? "Verified" : "Unverified"}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SuperadminGrantCard
          identityId={user.id}
          projectedIsSuperadmin={Boolean(user.isSuperadmin)}
          onChanged={refreshAfterGrantChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Chats</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalChats}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalMessages}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Chat Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCost)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Service Credits</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCredits}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Crown className="h-4 w-4" />
            Subscription Plan
          </CardTitle>
          {user.planOverrideByAdmin && (
            <Badge variant="default">Admin Override</Badge>
          )}
          {user.stripeSubscriptionId && !user.planOverrideByAdmin && (
            <Badge variant="default">Stripe Managed</Badge>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="pro_max">Pro Max</option>
            </select>
            <Button
              size="sm"
              onClick={handlePlanUpdate}
              disabled={isUpdatingPlan || selectedPlan === (user.subscriptionPlan || "free")}
            >
              {isUpdatingPlan ? "Updating..." : "Update Plan"}
            </Button>
            <span className="text-sm text-gray-500">
              Current: <Badge variant={user.subscriptionPlan === "free" ? "default" : "success"}>{user.subscriptionPlan || "free"}</Badge>
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("chats")}
            className={`${
              activeTab === "chats"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Chats
          </button>
          <button
            onClick={() => setActiveTab("service-usage")}
            className={`${
              activeTab === "service-usage"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Service Usage
          </button>
          <button
            onClick={() => setActiveTab("sessions")}
            className={`${
              activeTab === "sessions"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Sessions
          </button>
        </nav>
      </div>

      {activeTab === "chats" && (
        <Card>
          <CardHeader>
            <CardTitle>User Chats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-gray-600">
                Filter chats by time period
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Time Period:</label>
                <select
                  value={chatsDaysFilter}
                  onChange={(e) => handleChatsDaysFilterChange(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="1">Last 24 Hours</option>
                  <option value="7">Last 7 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 90 Days</option>
                </select>
              </div>
            </div>
            
            {isLoadingChats ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : chats.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No chats found for this user
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Messages</TableHead>
                    <TableHead>AI Messages</TableHead>
                    <TableHead>User Messages</TableHead>
                    <TableHead>Input Cost</TableHead>
                    <TableHead>Output Cost</TableHead>
                    <TableHead>Total Cost</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chats.map((chat) => (
                    <TableRow key={chat.chatId}>
                      <TableCell className="font-medium">
                        {chat.title || <span className="text-gray-400 italic">Untitled</span>}
                      </TableCell>
                      <TableCell>{chat.numberOfMessages}</TableCell>
                      <TableCell>{chat.numberOfAIMessages}</TableCell>
                      <TableCell>{chat.numberOfUserMessages}</TableCell>
                      <TableCell>{formatCurrency(chat.inputTokenCost)}</TableCell>
                      <TableCell>{formatCurrency(chat.outputTokenCost)}</TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(chat.totalCost)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(chat.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(chat.updatedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {!isLoadingChats && chats.length > 0 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-700">
                  Showing {((chatsPagination.page - 1) * chatsPagination.limit) + 1} to{' '}
                  {Math.min(chatsPagination.page * chatsPagination.limit, chatsPagination.total)} of{' '}
                  {chatsPagination.total} chats
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChatsPageChange(chatsPagination.page - 1)}
                    disabled={chatsPagination.page <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-700">
                    Page {chatsPagination.page} of {chatsPagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChatsPageChange(chatsPagination.page + 1)}
                    disabled={chatsPagination.page >= chatsPagination.pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "service-usage" && (
        <Card>
          <CardHeader>
            <CardTitle>Service Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-gray-600">
                Filter service usage by time period
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Time Period:</label>
                <select
                  value={serviceUsageDaysFilter}
                  onChange={(e) => handleServiceUsageDaysFilterChange(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="1">Last 24 Hours</option>
                  <option value="7">Last 7 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 90 Days</option>
                </select>
              </div>
            </div>
            
            {isLoadingServiceUsage ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : serviceUsage.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No service usage found for this user
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Credits Used</TableHead>
                    <TableHead>Timestamp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {serviceUsage.map((usage) => (
                    <TableRow key={usage.id}>
                      <TableCell className="font-medium">
                        {usage.purpose}
                      </TableCell>
                      <TableCell>{usage.creditsUsed}</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(usage.timestamp)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {!isLoadingServiceUsage && serviceUsage.length > 0 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-700">
                  Showing {((serviceUsagePagination.page - 1) * serviceUsagePagination.limit) + 1} to{' '}
                  {Math.min(serviceUsagePagination.page * serviceUsagePagination.limit, serviceUsagePagination.total)} of{' '}
                  {serviceUsagePagination.total} records
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleServiceUsagePageChange(serviceUsagePagination.page - 1)}
                    disabled={serviceUsagePagination.page <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-700">
                    Page {serviceUsagePagination.page} of {serviceUsagePagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleServiceUsagePageChange(serviceUsagePagination.page + 1)}
                    disabled={serviceUsagePagination.page >= serviceUsagePagination.pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "sessions" && (
        <div className="space-y-6">
          {/* Active Session Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                Active Session
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingActiveSession ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : activeSession?.session ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Started</p>
                      <p className="font-medium">{formatDate(activeSession.session.startedAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Expires</p>
                      <p className="font-medium">{formatDate(activeSession.session.expiresAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Session Cost</p>
                      <p className="font-medium">{formatCurrency(activeSession.session.totalCost)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Session Limit</p>
                      <p className="font-medium">{formatCurrency(activeSession.sessionLimit)}</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Usage</span>
                      <span className="font-medium">{activeSession.usagePercent}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all ${
                          activeSession.usagePercent >= 90
                            ? "bg-red-500"
                            : activeSession.usagePercent >= 70
                            ? "bg-yellow-500"
                            : "bg-blue-500"
                        }`}
                        style={{ width: `${Math.min(100, activeSession.usagePercent)}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Timer className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-500">Duration:</span>
                      <span>{activeSession.sessionDurationMinutes} min</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-500">Monthly Cost:</span>
                      <span>{formatCurrency(usageStatus?.monthlyCost || 0)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-500">Monthly Limit:</span>
                      <span>{formatCurrency(usageStatus?.monthlyLimit || activeSession.monthlyLimit)}</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const next = !showActiveSessionActivity;
                        setShowActiveSessionActivity(next);
                        if (next && id && activeSession.session) {
                          fetchActiveSessionActivity(id, activeSession.session.id);
                        }
                      }}
                      className="text-xs"
                    >
                      <List className="h-3.5 w-3.5 mr-1.5" />
                      {showActiveSessionActivity ? "Hide Activity" : "Show Activity"}
                    </Button>
                  </div>
                  {showActiveSessionActivity && (
                    <div className="border-t pt-4">
                      {isLoadingActiveActivity ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        </div>
                      ) : activeSessionActivity.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-2">No activity in this session</p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {activeSessionActivity.map((event, i) => (
                            <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-3 py-2">
                              <div className="flex items-center gap-3 min-w-0">
                                <span className="font-medium text-gray-700 truncate max-w-[160px]">{event.model}</span>
                                <span className="text-gray-400">|</span>
                                <span className="text-gray-500">{event.provider}</span>
                                {event.feature && (
                                  <>
                                    <span className="text-gray-400">|</span>
                                    <span className="text-gray-500">{event.feature}</span>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-gray-600">{formatCurrency(event.costUsd)}</span>
                                {event.totalTokens != null && (
                                  <span className="text-gray-400">{event.totalTokens.toLocaleString()} tok</span>
                                )}
                                <span className="text-gray-400">{formatDate(event.createdAt)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No active session</p>
                  {usageStatus?.isInCooldown && usageStatus.cooldownEndsAt && (
                    <p className="text-sm mt-1">
                      In cooldown until {formatDate(usageStatus.cooldownEndsAt)}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Previous Sessions Table */}
          <Card>
            <CardHeader>
              <CardTitle>Session History</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingSessions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No sessions found for this user
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>% of Limit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((session) => (
                      <React.Fragment key={session.id}>
                        <TableRow>
                          <TableCell>
                            <button
                              onClick={() => {
                                if (expandedSessionId === session.id) {
                                  setExpandedSessionId(null);
                                } else {
                                  setExpandedSessionId(session.id);
                                  if (id) fetchSessionActivity(id, session.id);
                                }
                              }}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              {expandedSessionId === session.id ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </TableCell>
                          <TableCell>
                            <Badge variant={session.isActive ? "success" : "default"}>
                              {session.isActive ? "Active" : "Expired"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDate(session.startedAt)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDate(session.expiresAt)}
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(session.totalCost)}
                          </TableCell>
                          <TableCell>
                            {activeSession?.sessionLimit ? (
                              <span>
                                {((session.totalCost / activeSession.sessionLimit) * 100).toFixed(1)}%
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                        {expandedSessionId === session.id && (
                          <TableRow>
                            <TableCell colSpan={6} className="bg-gray-50 p-0">
                              <div className="px-6 py-4">
                                {loadingSessionActivities[session.id] ? (
                                  <div className="flex items-center justify-center py-3">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                  </div>
                                ) : !sessionActivities[session.id] || sessionActivities[session.id].length === 0 ? (
                                  <p className="text-sm text-gray-500 text-center py-2">No activity in this session</p>
                                ) : (
                                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                                    {sessionActivities[session.id].map((event, i) => (
                                      <div key={i} className="flex items-center justify-between text-xs bg-white rounded px-3 py-2 border">
                                        <div className="flex items-center gap-3 min-w-0">
                                          <span className="font-medium text-gray-700 truncate max-w-[180px]">{event.model}</span>
                                          <span className="text-gray-300">|</span>
                                          <span className="text-gray-500">{event.provider}</span>
                                          {event.feature && (
                                            <>
                                              <span className="text-gray-300">|</span>
                                              <span className="text-gray-500">{event.feature}</span>
                                            </>
                                          )}
                                          {event.purpose && event.purpose !== event.feature && (
                                            <>
                                              <span className="text-gray-300">|</span>
                                              <span className="text-gray-400">{event.purpose}</span>
                                            </>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                          <span className="text-gray-600">{formatCurrency(event.costUsd)}</span>
                                          {event.totalTokens != null && (
                                            <span className="text-gray-400">{event.totalTokens.toLocaleString()} tok</span>
                                          )}
                                          <span className="text-gray-400">{formatDate(event.createdAt)}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              )}

              {!isLoadingSessions && sessions.length > 0 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Showing {((sessionsPagination.page - 1) * sessionsPagination.limit) + 1} to{' '}
                    {Math.min(sessionsPagination.page * sessionsPagination.limit, sessionsPagination.total)} of{' '}
                    {sessionsPagination.total} sessions
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSessionsPageChange(sessionsPagination.page - 1)}
                      disabled={sessionsPagination.page <= 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-700">
                      Page {sessionsPagination.page} of {sessionsPagination.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSessionsPageChange(sessionsPagination.page + 1)}
                      disabled={sessionsPagination.page >= sessionsPagination.pages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
